import { useMemo } from "react";
import { Show } from "shared/components";
import { DateInput } from "shared/components/forms/DateInput";
import { cx } from "shared/utils";
import {
  DateFilterKey,
  DateRange,
  getDateRangePresets,
  isDateRangeInverted
} from "../../utils/dateRanges";

const DATE_FIELD_CLASS =
  "relative [&>button]:absolute [&>button]:right-0 [&>button]:top-0";

// Presets plus From / To inputs for one date column, shared by the column header popover
// and the Filters drawer. Controlled: the parent owns the draft range.
export const DateRangeFields = ({
  idPrefix,
  dateKey,
  range,
  onChange
}: {
  idPrefix: string;
  dateKey: DateFilterKey;
  range: DateRange;
  onChange: (range: DateRange) => void;
}) => {
  const presets = useMemo(() => getDateRangePresets(dateKey), [dateKey]);

  const activePreset = useMemo(() => {
    const today = new Date();
    return presets.find((preset) => {
      const presetRange = preset.getRange(today);
      return presetRange.from === range.from && presetRange.to === range.to;
    })?.value;
  }, [presets, range.from, range.to]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {presets.map((preset) => (
          <button
            key={preset.value}
            type="button"
            className={cx(
              "rounded-lg border px-3 py-1.5 text-xs font-medium",
              activePreset === preset.value
                ? "border-primary-500 bg-primary-20 text-primary-600"
                : "border-alpha-100 text-content-1000"
            )}
            onClick={() => onChange(preset.getRange(new Date()))}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {/* DateInput renders label, boxes and its "Clear date" link as siblings; lift the
            link onto the label's row (top right) instead of a line of its own. */}
        <div className={DATE_FIELD_CLASS}>
          <DateInput
            id={`${idPrefix}-${dateKey}-from`}
            label="From"
            hideOptional
            clearable
            value={range.from ?? ""}
            onChange={(e) =>
              onChange({ ...range, from: e.target.value || undefined })
            }
          />
        </div>
        <div className={DATE_FIELD_CLASS}>
          <DateInput
            id={`${idPrefix}-${dateKey}-to`}
            label="To"
            hideOptional
            clearable
            value={range.to ?? ""}
            onChange={(e) =>
              onChange({ ...range, to: e.target.value || undefined })
            }
          />
        </div>
        <Show if={isDateRangeInverted(range)}>
          <p className="text-xs text-danger-500">
            The start date must be on or before the end date.
          </p>
        </Show>
      </div>
    </div>
  );
};
